import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ADMIN } from "@/constants/testIds";
import {
  Mail, Loader2, Save, RotateCcw, Send, LogOut, Lock, Check, AlertCircle, ArrowLeft, Plus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pt_admin_token";

const client = (token) =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  if (!token) {
    return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Login failed.");
    }
  };

  return (
    <div data-testid={ADMIN.loginPage} className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <div className="w-full max-w-sm rounded-3xl border border-border/70 bg-card/70 p-8 shadow-[var(--shadow-elev-2)] backdrop-blur-md">
        <div className="flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.1)]">
            <Lock className="h-5 w-5 text-neo-cyan" />
          </span>
          <div>
            <h1 className="font-display text-xl font-semibold">Admin Dashboard</h1>
            <p className="font-mono text-[11px] text-muted-foreground">Email templates</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            data-testid={ADMIN.password}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="h-11 w-full rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm outline-none focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
          />
          {error && <p data-testid={ADMIN.loginError} className="text-xs text-red-300">{error}</p>}
          <button
            data-testid={ADMIN.loginButton}
            type="submit"
            disabled={status === "loading"}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] text-sm font-medium shadow-[var(--glow-cyan)] hover:bg-[rgba(0,229,255,0.24)] disabled:opacity-60"
          >
            {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Log in
          </button>
        </form>
        <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [templates, setTemplates] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null); // {kind, text}
  const bodyRef = useRef(null);

  const api = client(token);

  const active = templates.find((t) => t.type === activeType);

  const handleAuthErr = (err) => {
    if (err?.response?.status === 401) onLogout();
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/templates");
      setTemplates(res.data.templates);
      if (res.data.templates.length) {
        const first = res.data.templates[0];
        setActiveType(first.type);
        setSubject(first.subject);
        setBody(first.body);
      }
    } catch (err) {
      handleAuthErr(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectType = (t) => {
    const tmpl = templates.find((x) => x.type === t);
    setActiveType(t);
    setSubject(tmpl.subject);
    setBody(tmpl.body);
    setStatus(null);
  };

  const flash = (kind, text) => {
    setStatus({ kind, text });
    if (kind === "success") setTimeout(() => setStatus(null), 4000);
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/admin/templates/${activeType}`, { subject, body });
      setTemplates((prev) => prev.map((t) => (t.type === activeType ? { ...t, subject, body, customized: true } : t)));
      flash("success", "Template saved.");
    } catch (err) {
      handleAuthErr(err);
      flash("error", err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy("");
    }
  };

  const resetDefault = async () => {
    setBusy("reset");
    try {
      const res = await api.post(`/admin/templates/${activeType}/reset`);
      setSubject(res.data.subject);
      setBody(res.data.body);
      setTemplates((prev) => prev.map((t) => (t.type === activeType ? { ...t, subject: res.data.subject, body: res.data.body, customized: false } : t)));
      flash("success", "Reset to default.");
    } catch (err) {
      handleAuthErr(err);
      flash("error", "Could not reset.");
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const res = await api.post(`/admin/templates/${activeType}/test`, { to: testEmail });
      flash("success", res.data.message || "Test email sent.");
    } catch (err) {
      handleAuthErr(err);
      flash("error", err?.response?.data?.detail || "Could not send test email.");
    } finally {
      setBusy("");
    }
  };

  const insertVar = (v) => {
    const token = `{{${v}}}`;
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid={ADMIN.dashboard} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.jpg" alt="Printopia" className="h-9 w-9 rounded-lg object-cover ring-1 ring-[rgba(0,229,255,0.35)]" />
            <div>
              <div className="font-display text-base font-semibold leading-tight">Email Templates</div>
              <div className="font-mono text-[10px] text-muted-foreground">Admin dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden items-center gap-1.5 rounded-lg border border-border/70 bg-secondary/50 px-3 py-2 text-sm text-muted-foreground hover:text-foreground sm:inline-flex">
              <ArrowLeft className="h-4 w-4" /> Site
            </Link>
            <button data-testid={ADMIN.logout} onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-secondary/50 px-3 py-2 text-sm text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[260px_1fr]">
        {/* Template list */}
        <aside className="space-y-2">
          {templates.map((t) => (
            <button
              key={t.type}
              data-testid={`admin-tab-${t.type}`}
              onClick={() => selectType(t.type)}
              className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                activeType === t.type
                  ? "border-[rgba(0,229,255,0.5)] bg-[rgba(0,229,255,0.12)] text-foreground"
                  : "border-border/70 bg-card/60 text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> {t.label}</span>
              {t.customized && <span className="font-mono text-[9px] uppercase text-neo-cyan">edited</span>}
            </button>
          ))}
        </aside>

        {/* Editor */}
        {active && (
          <main className="rounded-2xl border border-border/70 bg-card/60 p-6">
            <h2 className="font-display text-xl font-semibold">{active.label} email</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Customize the subject and body. The Printopia logo is added automatically at the bottom of every email.
            </p>

            {/* Variables */}
            <div className="mt-5">
              <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Available variables (click to insert)</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {active.variables.map((v) => (
                  <button
                    key={v}
                    onClick={() => insertVar(v)}
                    className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/50 px-2.5 py-1 font-mono text-[11px] text-foreground hover:border-[rgba(0,229,255,0.4)]"
                  >
                    <Plus className="h-3 w-3" /> {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-medium">Subject line</label>
              <input
                data-testid={ADMIN.subject}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-11 w-full rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm outline-none focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
              />
            </div>

            {/* Body */}
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium">Body text</label>
              <textarea
                data-testid={ADMIN.body}
                ref={bodyRef}
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full resize-y rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 py-3 font-mono text-[13px] leading-relaxed outline-none focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
              />
            </div>

            {status && (
              <div
                data-testid={ADMIN.status}
                className={`mt-4 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${
                  status.kind === "success"
                    ? "border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] text-foreground"
                    : "border-red-500/40 bg-red-500/10 text-red-200"
                }`}
              >
                {status.kind === "success" ? <Check className="h-4 w-4 text-neo-cyan" /> : <AlertCircle className="h-4 w-4" />}
                {status.text}
              </div>
            )}

            {/* Actions */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                data-testid={ADMIN.save}
                onClick={save}
                disabled={busy === "save"}
                className="inline-flex h-11 items-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] px-5 text-sm font-medium shadow-[var(--glow-cyan)] hover:bg-[rgba(0,229,255,0.24)] disabled:opacity-60"
              >
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
              </button>
              <button
                data-testid={ADMIN.reset}
                onClick={resetDefault}
                disabled={busy === "reset"}
                className="inline-flex h-11 items-center gap-2 rounded-[var(--radius)] border border-border/70 bg-secondary/60 px-4 text-sm font-medium hover:text-foreground disabled:opacity-60"
              >
                {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reset to default
              </button>
            </div>

            {/* Test send */}
            <div className="mt-8 rounded-2xl border border-border/60 bg-background/40 p-4">
              <div className="text-sm font-medium">Send a test email</div>
              <p className="mt-1 text-xs text-muted-foreground">Sends this template (with sample data) so you can preview it in a real inbox.</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  data-testid={ADMIN.testEmail}
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11 flex-1 rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm outline-none focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
                />
                <button
                  data-testid={ADMIN.testButton}
                  onClick={sendTest}
                  disabled={busy === "test" || !testEmail}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[var(--radius)] border border-[rgba(212,0,255,0.4)] bg-[rgba(212,0,255,0.16)] px-5 text-sm font-medium shadow-[var(--glow-magenta)] hover:bg-[rgba(212,0,255,0.24)] disabled:opacity-60"
                >
                  {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test
                </button>
              </div>
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
